import { Alert, Card, Collapse, Icon, IconButton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { UserContext } from "context/user";
import { getUproColor } from "utils/colors";
import { FLUJO_EVENTO, SECCIONES_DETALLE } from "./data/flujoEvento";
import { RUTA_LISTADO } from "./utils";

const ANCHO_FLUJO = 760;
const COLOR_NODO = { inicio: "uproGreen", paso: "uproBlue", decision: "uproYellow", fin: "uproGreen" };
const bordeSx = { border: ({ borders: { borderWidth, borderColor } }) => `${borderWidth[1]} solid ${borderColor}` };

// Línea vertical con punta de flecha entre dos nodos del diagrama.
function Conector({ etiqueta }) {
  return (
    <SoftBox display="flex" flexDirection="column" alignItems="center" aria-hidden="true">
      <SoftBox width="2px" height={etiqueta ? "14px" : "22px"} sx={{ backgroundColor: "#ced4da" }} />
      {etiqueta && <SoftTypography variant="caption" fontWeight="bold" color="secondary" lineHeight={1.4}>{etiqueta}</SoftTypography>}
      {etiqueta && <SoftBox width="2px" height="10px" sx={{ backgroundColor: "#ced4da" }} />}
      <Icon sx={{ color: "#ced4da", mt: "-9px", fontSize: "22px !important" }}>arrow_drop_down</Icon>
    </SoftBox>
  );
}

Conector.propTypes = { etiqueta: PropTypes.string };

function Marcador({ nodo }) {
  const color = getUproColor(COLOR_NODO[nodo.tipo]);
  if (nodo.tipo === "decision") {
    return (
      <SoftBox width="40px" height="40px" flexShrink={0} display="flex" alignItems="center" justifyContent="center">
        <SoftBox width="30px" height="30px" display="flex" alignItems="center" justifyContent="center"
          sx={{ backgroundColor: color, transform: "rotate(45deg)", borderRadius: "4px" }}>
          <Icon fontSize="small" sx={{ color: "#fff", transform: "rotate(-45deg)" }}>question_mark</Icon>
        </SoftBox>
      </SoftBox>
    );
  }
  return (
    <SoftBox width="40px" height="40px" flexShrink={0} borderRadius="50%" display="flex" alignItems="center" justifyContent="center"
      sx={{ backgroundColor: color, color: "#fff" }}>
      {nodo.numero
        ? <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#fff" }}>{nodo.numero}</SoftTypography>
        : <Icon>{nodo.icono}</Icon>}
    </SoftBox>
  );
}

Marcador.propTypes = { nodo: PropTypes.object.isRequired };

function Detalle({ detalle }) {
  const secciones = SECCIONES_DETALLE.filter((seccion) => detalle[seccion.id]?.length);
  return (
    <SoftBox display="grid" gap={1.5} sx={{ gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" } }}>
      {secciones.map((seccion) => (
        <SoftBox key={seccion.id} p={1.5} borderRadius="lg" sx={{ ...bordeSx, borderLeft: `3px solid ${getUproColor(seccion.color)}` }}>
          <SoftBox display="flex" alignItems="center" gap={0.75} mb={0.75}>
            <Icon fontSize="small" sx={{ color: getUproColor(seccion.color) }}>{seccion.icono}</Icon>
            <SoftTypography variant="button" fontWeight="bold">{seccion.titulo}</SoftTypography>
          </SoftBox>
          <SoftBox component="ul" m={0} pl={2.25}>
            {detalle[seccion.id].map((linea) => (
              <SoftTypography key={linea} component="li" variant="caption" color="text" display="list-item" mb={0.5}
                fontStyle={seccion.id === "ejemplos" ? "italic" : "normal"}>
                {linea}
              </SoftTypography>
            ))}
          </SoftBox>
        </SoftBox>
      ))}
    </SoftBox>
  );
}

Detalle.propTypes = { detalle: PropTypes.object.isRequired };

function Nodo({ nodo, abierto, alternar, irANodo, nodoRef }) {
  const idDetalle = `flujo-detalle-${nodo.id}`;
  const color = getUproColor(COLOR_NODO[nodo.tipo]);
  return (
    <SoftBox ref={nodoRef} borderRadius="xl" sx={{
      ...bordeSx,
      scrollMarginTop: "96px",
      backgroundColor: "#fff",
      borderColor: abierto ? color : undefined,
      boxShadow: abierto ? "0 4px 16px rgba(52, 71, 103, 0.12)" : "none",
      borderStyle: nodo.tipo === "decision" ? "dashed" : "solid",
      transition: "box-shadow 150ms, border-color 150ms",
    }}>
      <SoftBox component="button" type="button" onClick={alternar} aria-expanded={abierto} aria-controls={idDetalle}
        display="flex" alignItems="center" gap={1.5} p={1.5} width="100%" borderRadius="xl"
        sx={{
          border: 0, background: "transparent", textAlign: "left", font: "inherit", cursor: "pointer",
          "&:hover": { backgroundColor: "rgba(52, 71, 103, 0.04)" },
          "&:focus-visible": { outline: `2px solid ${color}`, outlineOffset: 2 },
        }}>
        <Marcador nodo={nodo} />
        <SoftBox minWidth={0} flex={1}>
          <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <SoftTypography variant="button" fontWeight="bold" color="dark">{nodo.titulo}</SoftTypography>
            {nodo.etiqueta && (
              <SoftBadge color={nodo.etiqueta === "Obligatorio" ? "uproPink" : "secondary"} badgeContent={nodo.etiqueta} size="xs" container />
            )}
          </SoftBox>
          <SoftTypography variant="caption" color="secondary" display="block">{nodo.resumen}</SoftTypography>
        </SoftBox>
        <Icon sx={{ color, transition: "transform 150ms", transform: abierto ? "rotate(180deg)" : "none" }}>expand_more</Icon>
      </SoftBox>

      <Collapse in={abierto} timeout="auto" unmountOnExit={false}>
        <SoftBox id={idDetalle} px={{ xs: 1.5, sm: 2 }} pb={2} pt={0.5}>
          <Detalle detalle={nodo.detalle} />
        </SoftBox>
      </Collapse>

      {nodo.tipo === "decision" && (
        <SoftBox px={{ xs: 1.5, sm: 2 }} pb={2} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1}>
          <SoftBox flex={1} display="flex" alignItems="center" gap={1} p={1.25} borderRadius="lg"
            sx={{ backgroundColor: "rgba(240, 5, 122, 0.06)" }}>
            <SoftBadge color="uproPink" badgeContent="Sí" size="xs" container />
            <SoftTypography variant="caption" color="text" sx={{ flex: 1 }}>{nodo.si.texto}</SoftTypography>
            <Tooltip title="Ir al paso 1" placement="top">
              <IconButton size="small" aria-label="Ir al paso Datos del evento" onClick={() => irANodo(nodo.si.destino)}
                sx={{ color: getUproColor("uproPink") }}>
                <Icon>replay</Icon>
              </IconButton>
            </Tooltip>
          </SoftBox>
          <SoftBox flex={1} display="flex" alignItems="center" gap={1} p={1.25} borderRadius="lg"
            sx={{ backgroundColor: "rgba(103, 193, 184, 0.1)" }}>
            <SoftBadge color="uproGreen" badgeContent="No" size="xs" container />
            <SoftTypography variant="caption" color="text">{nodo.no.texto}</SoftTypography>
          </SoftBox>
        </SoftBox>
      )}
    </SoftBox>
  );
}

Nodo.propTypes = {
  nodo: PropTypes.object.isRequired,
  abierto: PropTypes.bool.isRequired,
  alternar: PropTypes.func.isRequired,
  irANodo: PropTypes.func.isRequired,
  nodoRef: PropTypes.func.isRequired,
};

// Guía interactiva del alta de eventos. Solo para administradores (ver routes.js).
export default function FlujoEvento() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const esAdministrador = user?.tipoUser === "Administrador";
  const [abiertos, setAbiertos] = useState(() => new Set());
  const referencias = useRef({});

  const alternar = (id) => setAbiertos((prev) => {
    const siguiente = new Set(prev);
    if (siguiente.has(id)) siguiente.delete(id); else siguiente.add(id);
    return siguiente;
  });
  const todosAbiertos = abiertos.size === FLUJO_EVENTO.length;
  const alternarTodos = () => setAbiertos(todosAbiertos ? new Set() : new Set(FLUJO_EVENTO.map((nodo) => nodo.id)));
  const irANodo = (id) => {
    setAbiertos((prev) => new Set([...prev, id]));
    referencias.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar title="Cómo crear un evento" />
      <SoftBox py={3}>
        {!esAdministrador ? <Alert severity="error">Esta guía está disponible solo para administradores.</Alert> : (
          <Card>
            <SoftBox p={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <SoftBox display="flex" alignItems="center" gap={1} minWidth={0}>
                <IconButton aria-label="Volver al listado" onClick={() => navigate(RUTA_LISTADO)} sx={{ color: getUproColor("uproGreen") }}>
                  <Icon>arrow_back</Icon>
                </IconButton>
                <SoftBox minWidth={0}>
                  <SoftTypography variant="h6">Cómo crear un evento</SoftTypography>
                  <SoftTypography variant="caption" color="secondary">
                    Toque cada paso para ver qué se completa, sus restricciones y ejemplos.
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
              <SoftButton variant="outlined" color="primary" circular size="small" onClick={alternarTodos}>
                <Icon>{todosAbiertos ? "unfold_less" : "unfold_more"}</Icon>&nbsp;{todosAbiertos ? "Contraer todo" : "Expandir todo"}
              </SoftButton>
            </SoftBox>

            <SoftBox px={{ xs: 2, sm: 3 }} pb={4}>
              <SoftBox maxWidth={`${ANCHO_FLUJO}px`} mx="auto" role="list" aria-label="Pasos para crear un evento">
                {FLUJO_EVENTO.map((nodo, index) => (
                  <SoftBox key={nodo.id} role="listitem">
                    {index > 0 && <Conector etiqueta={FLUJO_EVENTO[index - 1].tipo === "decision" ? "No" : undefined} />}
                    <Nodo nodo={nodo} abierto={abiertos.has(nodo.id)} alternar={() => alternar(nodo.id)} irANodo={irANodo}
                      nodoRef={(elemento) => { referencias.current[nodo.id] = elemento; }} />
                  </SoftBox>
                ))}
                <SoftBox display="flex" justifyContent="center" mt={4}>
                  <SoftButton color="primary" circular onClick={() => navigate(`${RUTA_LISTADO}/nuevo`)}>
                    <Icon>add</Icon>&nbsp;Crear un evento
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </Card>
        )}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}
